import { useState } from "react";
import { Position } from "@rio-cloud/rio-uikit/mapTypes";
import { Route } from "src/types/routes";
import {
  getBoundingBox,
  getRandomValueToForceRerender,
  ICoordinates,
  IRoutePoint,
  ISandboxMarker,
} from "src/components/Map";
import { decode } from "src/utils/flexiblePolyline";
import { useHandleRoutes } from "./useHandleRoutes";

interface BoundingBox {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

interface Via {
  type?: string;
}

const positionDefault = {
  lat: -23.5475,
  lng: -46.63611,
};

export const usePublicRoutePolyline = () => {
  const [count, setCount] = useState(1);
  const [position, setPosition] = useState<ICoordinates | undefined>(positionDefault);
  const [segments, setSegments] = useState<IRoutePoint[]>([]);
  const [markers, setMarkers] = useState<ISandboxMarker[]>([]);
  const [markerNow, setMarkerNow] = useState<ISandboxMarker>();
  const [currencySegments, setCurrencySegments] = useState<IRoutePoint[]>([]);
  const [boundingBox, setBoundingBox] = useState<BoundingBox | undefined>();
  const [locationNow, setLocationNow] = useState("");
  const { handleGenerateMarker, handleSimpleLocation } = useHandleRoutes();

  const handleVehiclePosition = (location: Position) => {
    setLocationNow(`${handleSimpleLocation(location?.lat)},${handleSimpleLocation(location.lng)}`);
    setMarkerNow(
      handleGenerateMarker({
        lat: location?.lat,
        lng: location?.lng,
        mode: "street_truck",
      })
    );
    setPosition({
      lat: location?.lat,
      lng: location?.lng,
    });
  };

  const send = (positions: Position[]) => {
    const lastLocation = positions[positions.length - 1];
    if (lastLocation) {
      handleVehiclePosition(lastLocation);
    }
    setCurrencySegments(positions);
  };

  const handleRoutes = (routes: Route[], via?: Via[]) => {
    if (routes.length) {
      let ArrayOfShape: any[] = [];
      const route = routes?.[0];
      const data = route?.sections || [];

      const getPolylineArray = data.map((item) => {
        return {
          shape: decode(item.polyline),
        };
      });

      const routeMarkers = data.reduce((acum: ISandboxMarker[], curr, index, arr) => {
        if (index === 0) {
          const markerOrigin: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["start"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.departure.place.location.lat,
              lng: curr.departure.place.location.lng,
            },
          };
          acum.push(markerOrigin);
        }
        if (index === arr.length - 1) {
          const markerDestination: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["finish"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerDestination);
        } else {
          const markerStopover: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["rioglyph rioglyph-pushpin"],
              name: via?.[index]?.type,
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerStopover);
        }

        return acum;
      }, []);

      getPolylineArray.forEach((item) => {
        ArrayOfShape.push(item.shape.polyline);
      });

      const concatArrays = ArrayOfShape.flat();

      const getPoints = concatArrays.map((value) => {
        return {
          lat: value[0].toString(),
          lng: value[1].toString(),
        };
      });

      setSegments(getPoints as IRoutePoint[]);
      setMarkers(routeMarkers);

      if (getPoints?.length > 0) {
        const values = getBoundingBox(getPoints);
        const latSum = getPoints.reduce((acc, p) => acc + Number(p.lat), 0);
        const lngSum = getPoints.reduce((acc, p) => acc + Number(p.lng), 0);
        const center = {
          lat: latSum / getPoints.length,
          lng: lngSum / getPoints.length,
        };
        setPosition(center);
        const paddedValues = {
          top: values.top + 5,
          bottom: values.bottom - 0.1,
          left: values.left - 0.4,
          right: values.right + 0.4,
        };

        if (count === 1) {
          setBoundingBox(paddedValues);
        }
      } else if (routeMarkers?.length > 0) {
        setPosition(routeMarkers[0].position);
      }
    } else {
      setMarkers([]);
      setSegments([]);
    }

    setCount((prev) => prev + 1);
  };

  return {
    position,
    setPosition,
    segments,
    markers,
    setMarkers,
    markerNow,
    currencySegments,
    boundingBox,
    setBoundingBox,
    locationNow,
    handleRoutes,
    handleVehiclePosition,
    send,
  };
};
